import { useState, useEffect, useRef } from "react";
import { Function as FnIcon, PaperPlaneRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { api } from "../lib/api";

export const FORMULA_MODE_EVT = "rowtool:formula-mode";
export const FORMULA_COL_EVT = "rowtool:formula-col";

let FN_CACHE = null;

function currentWord(text, caret) {
  const before = text.slice(0, caret);
  if ((before.match(/\{/g) || []).length > (before.match(/\}/g) || []).length) return null;
  if ((before.match(/"/g) || []).length % 2) return null;
  const m = before.match(/[A-Za-zÀ-ÿ.]+$/);
  return m ? { word: m[0], start: caret - m[0].length } : null;
}

export function FormulaBuilder({ value, setValue, columns, onApply, busy }) {
  const ref = useRef(null);
  const [fns, setFns] = useState(FN_CACHE || []);
  const [caret, setCaret] = useState(value.length);
  const [hi, setHi] = useState(0);
  const [newCol, setNewCol] = useState("_formula");

  useEffect(() => {
    if (!FN_CACHE) api.formulaFunctions().then((r) => { FN_CACHE = r.functions; setFns(r.functions); }).catch(() => {});
    const el = ref.current;
    if (el) { el.focus(); el.setSelectionRange(value.length, value.length); }
    window.dispatchEvent(new CustomEvent(FORMULA_MODE_EVT, { detail: true }));
    return () => window.dispatchEvent(new CustomEvent(FORMULA_MODE_EVT, { detail: false }));
  }, []);

  const insertAt = (start, end, text) => {
    const next = value.slice(0, start) + text + value.slice(end);
    setValue(next);
    const pos = start + text.length;
    setCaret(pos);
    requestAnimationFrame(() => { const el = ref.current; if (el) { el.focus(); el.setSelectionRange(pos, pos); } });
  };

  useEffect(() => {
    const onCol = (e) => { const c = ref.current?.selectionStart ?? caret; insertAt(c, ref.current?.selectionEnd ?? c, `{${e.detail}}`); };
    window.addEventListener(FORMULA_COL_EVT, onCol);
    return () => window.removeEventListener(FORMULA_COL_EVT, onCol);
  });

  const cw = currentWord(value, caret);
  const sugg = cw ? fns.filter((f) => f.name.startsWith(cw.word.toUpperCase())).slice(0, 8) : [];
  const accept = (f) => insertAt(cw.start, caret, `${f.name}(`);

  const onKeyDown = (e) => {
    if (sugg.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => (h + 1) % sugg.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => (h - 1 + sugg.length) % sugg.length); return; }
      if (e.key === "Tab" || e.key === "Enter") { e.preventDefault(); accept(sugg[Math.min(hi, sugg.length - 1)]); return; }
    }
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); onApply(value, newCol); }
  };
  const track = (e) => { setCaret(e.target.selectionStart); setHi(0); };

  return (
    <div className="space-y-2" data-testid="formula-builder">
      <div className="flex items-center gap-2 text-xs text-[hsl(var(--accent))] font-semibold">
        <FnIcon size={16} weight="bold" /> Modalità formula · clicca l'intestazione di una colonna nella tabella per inserirla
      </div>
      <div className="relative flex gap-2">
        <Textarea ref={ref} value={value} rows={2} data-testid="formula-input"
          onChange={(e) => { setValue(e.target.value); track(e); }} onKeyUp={track} onClick={track} onKeyDown={onKeyDown}
          className="bg-muted resize-none grid-font text-sm" placeholder='=SE({F1_Premio} < 100; "Basso"; {F1_Premio})' />
        <Button onClick={() => onApply(value, newCol)} disabled={busy} data-testid="formula-apply-btn"
          className="bg-[hsl(var(--accent))] text-white gap-2 self-stretch"><PaperPlaneRight size={18} weight="fill" /></Button>
        {sugg.length > 0 && (
          <div className="absolute left-0 top-full mt-1 z-50 w-80 rounded-lg border border-border bg-popover shadow-lg py-1" data-testid="formula-suggestions">
            {sugg.map((f, i) => (
              <button key={f.name} type="button" data-testid={`formula-sugg-${f.name}`}
                onMouseDown={(e) => { e.preventDefault(); accept(f); }}
                className={`w-full text-left px-3 py-1.5 text-xs grid-font ${i === hi ? "bg-[hsl(var(--accent)/0.12)]" : "hover:bg-muted"}`}>
                <span className="font-bold">{f.name}</span> <span className="text-muted-foreground">{f.signature}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Nuova colonna</span>
        <Input value={newCol} onChange={(e) => setNewCol(e.target.value)} className="bg-muted h-8 w-48 text-xs" data-testid="formula-newcol-input" />
        <span className="text-[11px] text-muted-foreground ml-auto">Separatore «;» · testo tra "virgolette" · Tab per completare · Ctrl+Invio per applicare</span>
      </div>
      <div className="flex flex-wrap gap-1 max-h-24 overflow-auto" data-testid="formula-col-chips">
        {columns.map((c) => (
          <button key={c.key} type="button" data-testid={`formula-col-${c.key}`}
            onMouseDown={(e) => { e.preventDefault(); const s = ref.current?.selectionStart ?? caret; insertAt(s, ref.current?.selectionEnd ?? s, `{${c.key}}`); }}
            className="text-[11px] grid-font px-2 py-0.5 rounded-full border border-border hover:border-[hsl(var(--accent))] transition-colors">{c.key}</button>
        ))}
      </div>
    </div>
  );
}

export async function runFormula(job, formula, newCol, rowIds) {
  if (!formula.replace(/^=/, "").trim()) { toast.message("Scrivi una formula dopo ="); return null; }
  return api.verify(job.id, "formula", { formula, new_col: newCol }, rowIds);
}
